// === GENERATED:SCREEN:PRODUCT-SELECTION ===
// Generated by Sluitstraat Generator
// Custom card-based product selection UI with category grouping
// Step: Product Selection (Universal)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  WorkflowPageLayout,
  calculateTotalSteps,
} from '@sluitstraat/core';
import { ArrowRight, Check } from 'lucide-react';
import { productIcons } from '../../icons/productRegistry';
import { productSelectionStyles as styles } from '../../styles/commonStyles';
import { Badge } from '../../components/Badge';
import { WithHelpSidebar } from '../../components/HelpCard';
import { PRODUCT_CONTENT, getDekkingSummaryKeys, getCoverageInfoKeys } from '../../products/content';
import type { CoverageInfoItemKeys } from '../../products/content';
import { InfoTooltip } from '../../components/InfoTooltip';
import { getCurrentLocale } from '../../utils/currency';
import { COMBINATIONS } from './ongevallen/fields';
import { getProductOrder, buildEmptySelection } from '../../workflowConfig';
import { clearSaaSessionState } from '../../utils/workflowReset';

const SCREEN_ID = 'product-selection';

/** Available product IDs (selectable in the workflow) */
type ProductId = 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis';

/** All product IDs including coming-soon */
type AllProductId = ProductId | 'motor' | 'bromfiets' | 'fiets' | 'caravan' | 'camper' | 'boot';

/** Product selection state - maps product ID to selected status */
type ProductSelection = Record<ProductId, boolean>;

interface Product {
  id: AllProductId;
  nameKey: string;
  descriptionKey: string;
  icon: React.ComponentType<any>;
  featureKeys: string[];
  infoTitleKey?: string;
  infoItemKeys?: CoverageInfoItemKeys[];
  deductibleKey?: string;
  recommended: boolean;
  comingSoon: boolean;
}

interface Category {
  labelKey: string;
  products: Product[];
}

const CATEGORIES: Category[] = [
  {
    labelKey: 'products.categories.homeFamily',
    products: [
      {
        id: 'woonhuis',
        nameKey: PRODUCT_CONTENT.woonhuis.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.woonhuis.descriptionKey,
        icon: productIcons.woonhuis,
        featureKeys: getDekkingSummaryKeys('woonhuis'),
        infoTitleKey: PRODUCT_CONTENT.woonhuis.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('woonhuis'),
        deductibleKey: PRODUCT_CONTENT.woonhuis.deductibleKey,
        recommended: false,
        comingSoon: false,
      },
      {
        id: 'inboedel',
        nameKey: PRODUCT_CONTENT.inboedel.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.inboedel.descriptionKey,
        icon: productIcons.inboedel,
        featureKeys: getDekkingSummaryKeys('inboedel'),
        infoTitleKey: PRODUCT_CONTENT.inboedel.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('inboedel'),
        deductibleKey: PRODUCT_CONTENT.inboedel.deductibleKey,
        recommended: true,
        comingSoon: false,
      },
      {
        id: 'aansprakelijkheid',
        nameKey: PRODUCT_CONTENT.aansprakelijkheid.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.aansprakelijkheid.descriptionKey,
        icon: productIcons.aansprakelijkheid,
        featureKeys: getDekkingSummaryKeys('aansprakelijkheid'),
        infoTitleKey: PRODUCT_CONTENT.aansprakelijkheid.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('aansprakelijkheid'),
        deductibleKey: PRODUCT_CONTENT.aansprakelijkheid.deductibleKey,
        recommended: true,
        comingSoon: false,
      },
      {
        id: 'rechtsbijstand',
        nameKey: PRODUCT_CONTENT.rechtsbijstand.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.rechtsbijstand.descriptionKey,
        icon: productIcons.rechtsbijstand,
        featureKeys: getDekkingSummaryKeys('rechtsbijstand'),
        infoTitleKey: PRODUCT_CONTENT.rechtsbijstand.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('rechtsbijstand'),
        deductibleKey: PRODUCT_CONTENT.rechtsbijstand.deductibleKey,
        recommended: false,
        comingSoon: false,
      },
      {
        id: 'ongevallen',
        nameKey: PRODUCT_CONTENT.ongevallen.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.ongevallen.descriptionKey,
        icon: productIcons.ongevallen,
        featureKeys: getDekkingSummaryKeys('ongevallen'),
        infoTitleKey: PRODUCT_CONTENT.ongevallen.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('ongevallen'),
        deductibleKey: PRODUCT_CONTENT.ongevallen.deductibleKey,
        recommended: false,
        comingSoon: false,
      },
    ],
  },
  {
    labelKey: 'products.categories.mobility',
    products: [
      {
        id: 'personenauto',
        nameKey: PRODUCT_CONTENT.personenauto.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.personenauto.descriptionKey,
        icon: productIcons.personenauto,
        featureKeys: getDekkingSummaryKeys('personenauto'),
        infoTitleKey: PRODUCT_CONTENT.personenauto.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('personenauto'),
        deductibleKey: PRODUCT_CONTENT.personenauto.deductibleKey,
        recommended: true,
        comingSoon: false,
      },
      {
        id: 'motor',
        nameKey: 'products.motor.name',
        descriptionKey: 'products.motorDescription',
        icon: productIcons.motor,
        featureKeys: [
          'products.features.motorWaCasco',
          'products.features.motorTheft',
          'products.features.motorRoadside',
        ],
        recommended: false,
        comingSoon: true,
      },
      {
        id: 'bromfiets',
        nameKey: 'products.bromfiets.name',
        descriptionKey: 'products.bromfietsDescription',
        icon: productIcons.bromfiets,
        featureKeys: [
          'products.features.mopedWaCasco',
          'products.features.mopedTheft',
          'products.features.mopedRoadside',
        ],
        recommended: false,
        comingSoon: true,
      },
      {
        id: 'fiets',
        nameKey: 'products.fiets.name',
        descriptionKey: 'products.fietsDescription',
        icon: productIcons.fiets,
        featureKeys: [
          'products.features.bikeTheft',
          'products.features.bikeCollision',
          'products.features.bikeEbike',
        ],
        recommended: false,
        comingSoon: true,
      },
    ],
  },
  {
    labelKey: 'products.categories.leisure',
    products: [
      {
        id: 'doorlopendereis',
        nameKey: PRODUCT_CONTENT.doorlopendereis.displayNameKey,
        descriptionKey: PRODUCT_CONTENT.doorlopendereis.descriptionKey,
        icon: productIcons.doorlopendereis,
        featureKeys: getDekkingSummaryKeys('doorlopendereis'),
        infoTitleKey: PRODUCT_CONTENT.doorlopendereis.infoTitleKey,
        infoItemKeys: getCoverageInfoKeys('doorlopendereis'),
        deductibleKey: PRODUCT_CONTENT.doorlopendereis.deductibleKey,
        recommended: true,
        comingSoon: false,
      },
      {
        id: 'caravan',
        nameKey: 'products.caravan.name',
        descriptionKey: 'products.caravanDescription',
        icon: productIcons.caravan,
        featureKeys: [
          'products.features.caravanCollision',
          'products.features.caravanStorm',
          'products.features.caravanBreakIn',
        ],
        recommended: false,
        comingSoon: true,
      },
      {
        id: 'camper',
        nameKey: 'products.camper.name',
        descriptionKey: 'products.camperDescription',
        icon: productIcons.camper,
        featureKeys: [
          'products.features.camperCollision',
          'products.features.camperStorm',
          'products.features.camperBreakIn',
        ],
        recommended: false,
        comingSoon: true,
      },
      {
        id: 'boot',
        nameKey: 'products.boot.name',
        descriptionKey: 'products.bootDescription',
        icon: productIcons.boot,
        featureKeys: [
          'products.features.boatDamage',
          'products.features.boatTheft',
          'products.features.boatLiability',
        ],
        recommended: false,
        comingSoon: true,
      },
    ],
  },
];

const infoInterpolations = () => {
  const fmt = new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return { invalid: fmt.format(COMBINATIONS[0].invalid), overl: fmt.format(COMBINATIONS[0].overl) };
};

/** Render a single product card */
const ProductCard: React.FC<{
  product: Product;
  isSelected: boolean;
  isHovered: boolean;
  onToggle: () => void;
  onHover: (id: AllProductId | null) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ product, isSelected, isHovered, onToggle, onHover, t }) => {
  const Icon = product.icon;
  const isComingSoon = product.comingSoon;
  const deductible = product.deductibleKey && !isComingSoon
    ? `${t('products.deductibleLabel')}: ${t(product.deductibleKey)}`
    : null;
  const infoItems = isComingSoon
    ? []
    : [
        ...(product.infoItemKeys ?? []).map((item) => ({
          text: typeof item === 'string'
            ? t(item, infoInterpolations())
            : `${t(item.labelKey)}: ${t(item.textKey)}`,
        })),
        ...(deductible ? [{ text: deductible }] : []),
      ];

  return (
    <div style={styles.cardOuter}>
      {/* Badge: Recommended or Coming Soon */}
      {product.recommended && (
        <div style={styles.recommendedBadge}>
          <Badge variant="recommended" />
        </div>
      )}
      {isComingSoon && (
        <div style={styles.comingSoonBadge}>
          <Badge variant="comingSoon" />
        </div>
      )}

      <div
        style={{
          ...styles.productCard,
          ...(isComingSoon ? styles.productCardComingSoon : {}),
          ...(isSelected && !isComingSoon ? styles.productCardSelected : {}),
          ...(isHovered && !isSelected && !isComingSoon ? styles.productCardHovered : {}),
        }}
        onClick={() => !isComingSoon && onToggle()}
        onMouseEnter={() => onHover(product.id)}
        onMouseLeave={() => onHover(null)}
        role="button"
        tabIndex={isComingSoon ? -1 : 0}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if ((e.key === 'Enter' || e.key === ' ') && !isComingSoon) {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-pressed={isSelected}
        aria-disabled={isComingSoon}
      >
        {/* Selection circle top-right */}
        <div style={isSelected && !isComingSoon ? styles.selectedBadge : styles.unselectedBadge}>
          {isSelected && !isComingSoon ? (
            <Check style={styles.selectedCheck} />
          ) : (
            <div style={styles.emptyCircle} />
          )}
        </div>

        <div style={styles.cardHeader}>
          {/* Icon — the tile stays put on selection; the 2px border + filled check carry that state */}
          <div style={styles.iconContainer}>
            <Icon
              size={28}
              weight="duotone"
              color="var(--secondary-color, #F51C17)"
              style={isComingSoon ? styles.iconComingSoon : undefined}
            />
          </div>

          <div style={styles.productNameRow}>
            <h3 style={{ ...styles.productName, ...(isComingSoon ? styles.comingSoonText : {}) }}>{t(product.nameKey)}</h3>
            {infoItems.length > 0 && product.infoTitleKey && (
              <InfoTooltip title={t(product.infoTitleKey)} items={infoItems} />
            )}
          </div>

          <p style={{ ...styles.productDescription, ...(isComingSoon ? styles.comingSoonText : {}) }}>
            {t(product.descriptionKey)}
          </p>
        </div>

        <div style={styles.cardContent}>
          <ul style={styles.productFeatures}>
            {product.featureKeys.map((featureKey, idx) => (
              <li key={idx} style={styles.featureItem}>
                <Check style={isComingSoon ? styles.featureCheckComingSoon : styles.featureCheckDefault} />
                <span style={isComingSoon ? styles.featureTextComingSoon : styles.featureTextDefault}>
                  {t(featureKey)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const ProductSelectionScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, setSelectedProducts } = useWorkflow();

  // Initialize from context or localStorage
  const [selection, setSelection] = useState<ProductSelection>(() => {
    if (selectedProducts && Object.keys(selectedProducts).length > 0) {
      return selectedProducts as ProductSelection;
    }
    const saved = getStepData(SCREEN_ID);
    return saved?.selectedProducts || buildEmptySelection();
  });

  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [hoveredCard, setHoveredCard] = useState<AllProductId | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  // Clear stored IDs when starting a fresh workflow (but not when navigating back within the workflow)
  useEffect(() => {
    const workflowCompleted = sessionStorage.getItem('workflow_completed') === 'true';

    // A product still selected in this session's restored context = the user navigated back
    // within a live workflow: keep the backoffice session IDs. The relation is reused (it must
    // never duplicate once made) and the summary chain re-runs only when product data changed.
    // The old workflow_current_page heuristic broke when most screens stopped writing that key:
    // any return to product-selection looked like a fresh start, cleared vp_idnr, and the next
    // summary visit created a NEW relation. Fresh start = empty context (new tab / reload) or
    // a completed workflow.
    const isNavigatingBackWithinWorkflow =
      Object.values(selectedProducts || {}).some(Boolean) && !workflowCompleted;

    const storedRelationId = sessionStorage.getItem('vp_idnr');
    const storedCalculationId = sessionStorage.getItem('calculation_id');
    const storedQuoteId = sessionStorage.getItem('quote_id');
    const hasStoredIds = storedRelationId || storedCalculationId || storedQuoteId;

    if (hasStoredIds && !isNavigatingBackWithinWorkflow) {
      clearSaaSessionState();
    }

    if (workflowCompleted) {
      sessionStorage.removeItem('workflow_completed');
    }

    sessionStorage.setItem('workflow_current_page', 'product-selection');
    sessionStorage.setItem('workflow_session_active', 'true');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only session reset; selectedProducts is read once
  }, []);

  const toggleProduct = useCallback((productId: AllProductId) => {
    // Only toggle if it's a real, configured (selectable) product
    if (!(getProductOrder() as string[]).includes(productId)) return;
    // Committed to the workflow on every toggle, not only on Volgende: the stepper's forward
    // gate (and every other selectedProducts reader) must see the live selection.
    const next: ProductSelection = { ...selection, [productId as ProductId]: !selection[productId as ProductId] };
    setSelection(next);
    setSelectedProducts?.(next);
    setShowValidationError(false);
  }, [selection, setSelectedProducts]);

  const handleNext = useCallback(() => {
    const hasSelection = Object.values(selection).some((val) => val === true);
    if (!hasSelection) {
      setShowValidationError(true);
      return;
    }
    saveStepData(SCREEN_ID, { selectedProducts: selection });
    if (setSelectedProducts) {
      setSelectedProducts(selection);
    }
    navigate('/workflow/client-information');
  }, [selection, saveStepData, setSelectedProducts, navigate]);

  const selectedCount = Object.values(selection).filter((v) => v).length;

  const allowedProducts = getProductOrder() as string[];
  const visibleCategories = CATEGORIES
    .map((category) => ({
      ...category,
      products: category.products.filter((p) => p.comingSoon || allowedProducts.includes(p.id)),
    }))
    .filter((category) => category.products.length > 0);

  return (
    <WorkflowPageLayout currentStep={1} totalSteps={calculateTotalSteps(selection)}>
      <main style={styles.page}>
        <div style={styles.container}>
          <WithHelpSidebar showSelection={false} showTrust>
            {/* Header lives in the left column; the sidebar top-aligns with it */}
            <header style={styles.contentHeader}>
              <h1 style={styles.title}>{t('products.selectCoverage')}</h1>
              <p style={styles.subtitle}>{t('products.selectSubtitle')}</p>
            </header>

            {showValidationError && (
              <div style={styles.validationError}>{t('products.selectAtLeastOne')}</div>
            )}

            {/* Desktop/Tablet: Category Groups with grid */}
            {visibleCategories.map((category, catIdx) =>(
              <div key={catIdx} style={styles.categorySection} className="category-section-desktop">
                <div style={styles.categoryHeader}>
                  <span style={styles.categoryLabel}>{t(category.labelKey)}</span>
                  <div style={styles.categoryLine} />
                </div>
                <div className="products-grid-responsive" style={styles.categoryGrid}>
                  {category.products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      isSelected={!product.comingSoon && selection[product.id as ProductId] === true}
                      isHovered={hoveredCard === product.id}
                      onToggle={() => toggleProduct(product.id)}
                      onHover={setHoveredCard}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Mobile: Accordion categories */}
            <div className="category-mobile-accordion">
              {visibleCategories.map((category, catIdx) =>{
                const isExpanded = expandedCategories.has(catIdx);
                const selectedInCategory = category.products.filter(
                  (p) => !p.comingSoon && selection[p.id as ProductId] === true
                ).length;
                const hasSelection = selectedInCategory > 0;

                return (
                  <div key={catIdx} style={styles.mobileCategory}>
                    <button
                      className={`mobile-category-toggle${hasSelection ? ' has-selection' : ''}`}
                      onClick={() => {
                        setExpandedCategories((prev) => {
                          const next = new Set(prev);
                          if (next.has(catIdx)) next.delete(catIdx);
                          else next.add(catIdx);
                          return next;
                        });
                      }}
                    >
                      <div style={styles.mobileCategoryToggleInner}>
                        <div style={styles.mobileCategoryToggleRow}>
                          <span className="mobile-category-toggle-label">{t(category.labelKey)}</span>
                          <div className="mobile-category-toggle-meta">
                            {hasSelection && (
                              <span className="mobile-category-count">{selectedInCategory}</span>
                            )}
                            <svg className={`mobile-category-chevron${isExpanded ? ' open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </div>
                        </div>

                        {/* Product mini-previews: show when collapsed */}
                        {!isExpanded && (
                          <div className="mobile-category-previews">
                            {category.products.map((product) => {
                              const Icon = product.icon;
                              const isSelected = !product.comingSoon && selection[product.id as ProductId] === true;
                              return (
                                <div key={product.id} className={`mobile-product-chip${isSelected ? ' selected' : ''}${product.comingSoon ? ' coming-soon' : ''}`}>
                                  <Icon size={16} weight="duotone" color={isSelected ? 'var(--primary-color, #056666)' : product.comingSoon ? '#d1d5db' : '#6b7280'} />
                                  <span>{t(product.nameKey)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="mobile-category-cards">
                        {category.products.map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            isSelected={!product.comingSoon && selection[product.id as ProductId] === true}
                            isHovered={hoveredCard === product.id}
                            onToggle={() => toggleProduct(product.id)}
                            onHover={setHoveredCard}
                            t={t}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Section with Continue Button */}
            <div style={styles.bottomSection} className="bottom-section-responsive">
              <div style={styles.selectionSummary} className="selection-summary-responsive">
                {selectedCount === 0 ? (
                  <span>{t('products.noProductsSelected')}</span>
                ) : (
                  <span>{t('products.productsSelected', { count: selectedCount })}</span>
                )}
              </div>

              <nav style={styles.navigationSingle} className="navigation-responsive">
                <button
                  onClick={handleNext}
                  disabled={selectedCount === 0}
                  style={{
                    ...styles.buttonPrimary,
                    ...(selectedCount === 0 ? styles.buttonDisabled : {}),
                  }}
                >
                  {t('common.nextPlain')}
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              </nav>
            </div>
          </WithHelpSidebar>

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default ProductSelectionScreen;
